/** Threads text contract, mirrored in website/backend. Keep both copies and boundary tests in sync.
 * Meta: https://developers.facebook.com/documentation/threads/posts/
 * Text has a 500-character budget; emoji graphemes consume their UTF-8 byte length.
 */
export const THREADS_TEXT_LIMIT = 500;
const segmenter = new Intl.Segmenter('en', {granularity: 'grapheme'});
const encoder = new TextEncoder();
const emoji = /[\p{Extended_Pictographic}\p{Regional_Indicator}\u20e3]/u;
const emojiModifier = /\p{Emoji_Modifier}/u;

type ThreadsTextPost = {
    socialMediaPlatform: string;
    caption?: string;
    description?: string;
    firstComment?: string;
    isRepost?: boolean;
};

/** Count emoji as UTF-8 bytes, other text as Unicode code points (including combining marks). */
export function countThreadsText(text: string): number {
    let count = 0;
    for (const {segment} of segmenter.segment(text)) {
        count += (emoji.test(segment)) ? encoder.encode(segment).length : Array.from(segment).reduce(
            (total, character) => total + ((emojiModifier.test(character)) ? encoder.encode(character).length : 1), 0,
        );
    }
    return count;
}

/** Match the caption/description separator used by the Threads publishing worker. */
export function combineThreadsText(caption?: string, description?: string): string {
    return [caption, description].map((value) => value?.trim() ?? '').filter(Boolean).join(' ');
}

/** Shorten prose at a grapheme boundary, reserving space for the complete article URLs and an ellipsis.
 * Overlong individual URLs are omitted, never emitted as broken links. Multiple links retain their order.
 */
export function truncateThreadsText(text: string): string {
    if (countThreadsText(text) <= THREADS_TEXT_LIMIT) return text;
    const links: Array<string> = [];
    // Match once: terminal sentence punctuation belongs to prose, not the reserved URL.
    const prose = text.replace(/https?:\/\/[^\s]+/giu, (rawLink) => {
        let end = rawLink.length;
        const unmatchedClosers: Record<string, number> = {};
        for (const [closing, opening] of Object.entries({')': '(', ']': '[', '}': '{'})) {
            unmatchedClosers[closing] = rawLink.split(closing).length - rawLink.split(opening).length;
        }
        while (end > 0) {
            const last = rawLink[end - 1];
            const unmatched = unmatchedClosers[last] ?? 0;
            if ((unmatched <= 0) && !/[.,;!?]/u.test(last)) break;
            if (unmatched > 0) unmatchedClosers[last] = unmatched - 1;
            end--;
        }
        const link = rawLink.slice(0, end);
        links.push(link);
        return rawLink.slice(link.length);
    }).trim();
    const keptLinks: Array<string> = [];
    for (const link of links) {
        if (countThreadsText([...keptLinks, link].join(' ')) <= THREADS_TEXT_LIMIT) keptLinks.push(link);
    }
    const reservedLinks = keptLinks.join(' ');
    // Prefer a complete link when there is no room for prose plus an ellipsis separator.
    if (countThreadsText(reservedLinks) > THREADS_TEXT_LIMIT - 2) return reservedLinks;
    const suffix = (keptLinks.length) ? ` ${reservedLinks}` : '';
    const budget = THREADS_TEXT_LIMIT - countThreadsText(`…${suffix}`);
    let prefix = '';
    let count = 0;
    for (const {segment} of segmenter.segment(prose)) {
        const size = countThreadsText(segment);
        if (count + size > budget) break;
        prefix += segment;
        count += size;
    }
    return `${prefix.trimEnd()}…${suffix}`;
}

/** Reject authored overflow before storing or activating a scheduling task. Replies have their own budget. */
export function assertThreadsPostText(post: ThreadsTextPost): void {
    if ((post.socialMediaPlatform !== 'threads') || post.isRepost) return;
    if (countThreadsText(combineThreadsText(post.caption, post.description)) > THREADS_TEXT_LIMIT) {
        throw new Error('THREADS_TEXT_TOO_LONG');
    }
    if (countThreadsText(post.firstComment?.trim() ?? '') > THREADS_TEXT_LIMIT) {
        throw new Error('THREADS_REPLY_TOO_LONG');
    }
}

/** Bound automated posts before persistence; leave other platforms and already valid fields untouched. */
export function normalizeThreadsPostText(post: ThreadsTextPost): void {
    if ((post.socialMediaPlatform !== 'threads') || post.isRepost) return;
    const combined = combineThreadsText(post.caption, post.description);
    if (countThreadsText(combined) > THREADS_TEXT_LIMIT) {
        post.caption = truncateThreadsText(combined);
        post.description = '';
    }
    if (post.firstComment && (countThreadsText(post.firstComment) > THREADS_TEXT_LIMIT)) {
        post.firstComment = truncateThreadsText(post.firstComment.trim());
    }
}

/** Expand Threads macros for validation. Draft dates/times reserve the longest format used by the worker. */
export function expandThreadsMacros(text: string, pageName: string, time = '11:59 PM', date = '31.12.2026'): string {
    return text.trim().replaceAll('@{{pageName}}', pageName)
        .replaceAll('@{{postingTime}}', time).replaceAll('@{{postingDate}}', date)
        .replaceAll('@{{platform}}', 'Threads').replaceAll('@{{postingType}}', 'Feed');
}
